"use client";

import { createContext, useContext, useCallback, useState, type ReactNode } from "react";

interface AnnouncerContextType {
  message: string;
  announce: (message: string) => void;
}

export const AnnouncerContext = createContext<AnnouncerContextType | undefined>(undefined);

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState("");

  const announce = useCallback((newMessage: string) => {
    // Clear first, then set — this ensures screen readers detect the change
    // even if the same message is announced twice in a row.
    setMessage("");
    // Use requestAnimationFrame to ensure the empty string is committed to DOM
    // before setting the new message. Without this, React may batch the two
    // setState calls and the screen reader won't detect a change.
    requestAnimationFrame(() => {
      setMessage(newMessage);
    });
  }, []);

  return (
    <AnnouncerContext.Provider value={{ message, announce }}>
      {children}
    </AnnouncerContext.Provider>
  );
}

export function useAnnounce(): (message: string) => void {
  const context = useContext(AnnouncerContext);
  if (!context) {
    throw new Error("useAnnounce must be used within AnnouncerProvider");
  }
  return context.announce;
}
